// La campana (D52): junta en una sola lista lo que le importa a esta persona, segun sus permisos. Son los correos
// nuevos de los buzones que puede leer (D38), los clientes que respondieron sus casos, los casos que se quedaron sin
// respuesta, los seguimientos de hoy, sus reuniones proximas y las invitaciones nuevas (D49).
// Funciones puras, con pruebas. La lista se arma de nuevo en cada lectura, sin guardar eventos. Cada aviso tiene un
// id estable y una fecha, y "leido" es la fecha en que la persona lo vio.
import { type Caso, type CopiaReciente, HORAS_DESHACER, SLA_MINUTOS } from '../api/casos';
import { type Cita } from '../api/citas';
import { remitente } from './correo';
import { senalDe } from './senalesCaso';

export type TipoAviso = 'correo' | 'cliente' | 'sla' | 'seguimiento' | 'reunion' | 'invitacion' | 'equipo' | 'copia' | 'proyecto' | 'horas';
export type Urgencia = 0 | 1 | 2 | 3;
export type Aviso = {
  id: string;
  tipo: TipoAviso;
  fecha: string;
  titulo: string;
  detalle: string;
  urgencia: Urgencia;
  // A donde lleva al tocarlo.
  ir: { a: 'correo'; hiloId: string } | { a: 'caso'; casoId: string } | { a: 'cita'; citaId: string } | { a: 'ruta'; ruta: string };
  enlace?: string;
  // Copia al caso desde mi buzon personal (capa 3): se puede deshacer durante 24 h.
  copia?: CopiaReciente;
};
export type Sonido = 'suave' | 'campanilla' | 'reunion' | 'ninguno';

export type PrefNotif = {
  notificar: boolean;
  sonido: boolean;
  correos: boolean;
  casos: boolean;
  reuniones: '15y5' | '5' | 'no';
  invitaciones: boolean;
  modoSonido: 'tipo' | 'suave' | 'reuniones';
  noMolestar: boolean;
  desde: string;
  hasta: string;
};
export const PREF_NOTIF: PrefNotif = {
  notificar: true,
  sonido: true,
  correos: true,
  casos: true,
  reuniones: '15y5',
  invitaciones: true,
  modoSonido: 'tipo',
  noMolestar: false,
  desde: '19:00',
  hasta: '07:00',
};

const MIN = 60_000;
const iso = (t: number) => new Date(t).toISOString();

/* ---------------- fuentes ---------------- */

type HiloAviso = { id: string; ultimo: { asunto: string; receivedAt: string; participantes: { role: string; handle: string; displayName?: string | null }[] } };

// Correos sin leer (ya filtrados con la misma regla del contador de Correos) de los ultimos 2 dias.
export function avisosDeCorreos(hilos: HiloAviso[], ahora = Date.now()): Aviso[] {
  return hilos
    .filter((h) => ahora - Date.parse(h.ultimo.receivedAt) < 2 * 24 * 60 * MIN)
    .slice(0, 40)
    .map((h) => {
      const de = remitente(h.ultimo);
      return {
        id: `correo:${h.id}`,
        tipo: 'correo',
        fecha: h.ultimo.receivedAt,
        titulo: de?.displayName || de?.handle || 'Correo nuevo',
        detalle: h.ultimo.asunto || '(sin asunto)',
        urgencia: 1,
        ir: { a: 'correo', hiloId: h.id },
      };
    });
}

const quien = (c: Caso) => c.persona?.nombre || c.empresa?.nombre || c.correoCliente || 'El cliente';
const tituloCaso = (c: Caso) => `#${c.numero ?? ''} ${c.asunto}`.trim();

// Casos de esta persona (o sin tomar en sus grupos), con la misma senal que pinta Mi dia.
export function avisosDeCasos(casos: Caso[], miembroId: string | null | undefined, misGrupos: string[], ahora = Date.now()): Aviso[] {
  const out: Aviso[] = [];
  for (const c of casos) {
    const mio = c.asignadoId ? c.asignadoId === miembroId : misGrupos.includes(c.grupo);
    if (!mio) continue;
    const s = senalDe(c, ahora);
    if (s === 'respondio' && c.ultimoCliente)
      out.push({ id: `caso:${c.id}:${c.ultimoCliente}`, tipo: 'cliente', fecha: c.ultimoCliente, titulo: `${quien(c)} respondió`, detalle: tituloCaso(c), urgencia: 2, ir: { a: 'caso', casoId: c.id } });
    if (s === 'espera') {
      const base = c.ultimoCliente ?? c.creado;
      out.push({ id: `sla:${c.id}:${base}`, tipo: 'sla', fecha: iso(Date.parse(base) + SLA_MINUTOS * MIN), titulo: 'Caso sin respuesta', detalle: `${quien(c)} · ${tituloCaso(c)}`, urgencia: 3, ir: { a: 'caso', casoId: c.id } });
    }
    if (s === 'seguimiento' && c.seguimientoEn) {
      const fecha = Math.min(Date.parse(c.seguimientoEn), ahora);
      out.push({ id: `seg:${c.id}:${c.seguimientoEn}`, tipo: 'seguimiento', fecha: iso(fecha), titulo: 'Seguimiento para hoy', detalle: tituloCaso(c), urgencia: 1, ir: { a: 'caso', casoId: c.id } });
    }
  }
  return out;
}

const viva = (c: Cita) => c.estado !== 'CANCELADA' && c.estado !== 'REALIZADA' && c.estado !== 'NO_ASISTIO';
const hora = (t: string) => new Date(t).toLocaleTimeString('es-CO', { hour: 'numeric', minute: '2-digit' });

// Lo que escribi con el cliente desde mi buzon y la app copio a un caso (capa 3): «Tu respuesta a Bestsol se agregó al
// caso #N», con Deshacer mientras no pasen 24 h.
export function avisosDeCopias(copias: CopiaReciente[], ahora = Date.now()): Aviso[] {
  return copias
    .filter((c) => ahora - Date.parse(c.fecha) < HORAS_DESHACER * 3_600_000)
    .map((c) => ({
      id: `copia:${c.casoId}:${c.hiloId}`,
      tipo: 'copia' as const,
      fecha: c.fecha,
      titulo: `${c.cuantas === 1 ? 'Tu respuesta' : `Tus ${c.cuantas} respuestas`} a ${c.cliente} se ${c.cuantas === 1 ? 'agregó' : 'agregaron'} al caso #${c.numero ?? '—'}`,
      detalle: 'Lo ve todo el grupo · puedes deshacerlo durante 24 h',
      urgencia: 1 as const,
      ir: { a: 'caso' as const, casoId: c.casoId },
      copia: c,
    }));
}

// Reuniones mias (o del equipo sin responsable) que empiezan pronto: un aviso a 15 min y otro a 5 min.
export function avisosDeReuniones(citas: Cita[], miembroId: string | null | undefined, anticipacion: PrefNotif['reuniones'], ahora = Date.now()): Aviso[] {
  if (anticipacion === 'no') return [];
  const out: Aviso[] = [];
  for (const c of citas) {
    if (!viva(c) || (c.responsableId && c.responsableId !== miembroId)) continue;
    const falta = (Date.parse(c.inicio) - ahora) / MIN;
    if (falta < -10) continue;
    const quienes = [c.persona?.nombre, c.empresa?.nombre].filter(Boolean).join(' · ');
    const base = { titulo: c.titulo, ir: { a: 'cita' as const, citaId: c.id }, enlace: c.enlace || undefined };
    if (falta <= 5) {
      const det = falta > 0.5 ? `Empieza en ${Math.ceil(falta)} min` : falta >= -0.5 ? 'Empieza ahora' : `Empezó hace ${Math.round(-falta)} min`;
      out.push({ ...base, id: `reunion5:${c.id}:${c.inicio}`, tipo: 'reunion', fecha: iso(Date.parse(c.inicio) - 5 * MIN), detalle: [det, quienes].filter(Boolean).join(' · '), urgencia: 3 });
    } else if (falta <= 15 && anticipacion === '15y5') {
      out.push({ ...base, id: `reunion15:${c.id}:${c.inicio}`, tipo: 'reunion', fecha: iso(Date.parse(c.inicio) - 15 * MIN), detalle: [`Empieza en ${Math.ceil(falta)} min · ${hora(c.inicio)}`, quienes].filter(Boolean).join(' · '), urgencia: 2 });
    }
  }
  return out;
}

// Invitaciones y reuniones de 3CX que llegaron en los ultimos 3 dias: las mias y las del equipo sin responsable.
export function avisosDeInvitaciones(citas: Cita[], miembroId: string | null | undefined, ahora = Date.now()): Aviso[] {
  const out: Aviso[] = [];
  for (const c of citas) {
    if (c.origen === 'MANUAL' || !c.creado || !viva(c) || Date.parse(c.inicio) < ahora) continue;
    if (ahora - Date.parse(c.creado) > 3 * 24 * 60 * MIN) continue;
    const cuando = new Date(c.inicio).toLocaleString('es-CO', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
    if (c.responsableId && c.responsableId === miembroId)
      out.push({ id: `inv:${c.id}`, tipo: 'invitacion', fecha: c.creado, titulo: `Invitación: ${c.titulo}`, detalle: `${cuando} · ya está en tu agenda`, urgencia: 1, ir: { a: 'cita', citaId: c.id } });
    else if (!c.responsableId)
      out.push({ id: `equipo:${c.id}`, tipo: 'equipo', fecha: c.creado, titulo: `Sin tomar: ${c.titulo}`, detalle: `${cuando} · en "Del equipo"`, urgencia: 0, ir: { a: 'cita', citaId: c.id } });
  }
  return out;
}

/* ---------------- lista, leidos y alertas ---------------- */

// Proyectos (D96): al responsable, cuando su proyecto pasa el 80 % de las horas o se atrasa. Un aviso por proyecto y
// situacion (el id no cambia mientras siga igual, asi no se repite); la fecha es el lunes de la semana en que se ve.
export function avisosDeProyectos(
  proyectos: { id: string; nombre: string; responsableId: string | null; alertas: { texto: string; tono: 'bad' | 'warn' }[] }[],
  miembroId: string | undefined,
  lunes: string,
): Aviso[] {
  if (!miembroId) return [];
  return proyectos
    .filter((p) => p.responsableId === miembroId && p.alertas.some((a) => a.tono === 'bad' || /horas/.test(a.texto)))
    .map((p) => {
      const graves = p.alertas.filter((a) => a.tono === 'bad' || /horas/.test(a.texto));
      return {
        id: `proyecto:${p.id}:${graves.map((a) => (/Atrasado/.test(a.texto) ? 'atraso' : /valor/.test(a.texto) ? 'valor' : a.tono === 'bad' ? 'pasado' : '80')).join('-')}`,
        tipo: 'proyecto' as const,
        fecha: lunes,
        titulo: `Proyecto ${p.nombre}`,
        detalle: graves.map((a) => a.texto).join(' · '),
        urgencia: (graves.some((a) => a.tono === 'bad') ? 2 : 1) as Urgencia,
        ir: { a: 'ruta' as const, ruta: `/proyectos/${p.id}` },
      };
    });
}

// Horas (D96): el lunes, a quien registro menos de la mitad de la meta la semana anterior.
export function avisoDeHoras(minutosSemanaAnterior: number, meta: number, lunes: string, ahora = Date.now()): Aviso[] {
  if (minutosSemanaAnterior >= meta / 2 || ahora - Date.parse(lunes) > 3 * 86_400_000) return [];
  const h = Math.round((minutosSemanaAnterior / 60) * 10) / 10;
  return [
    {
      id: `horas:${lunes.slice(0, 10)}`,
      tipo: 'horas',
      fecha: lunes,
      titulo: 'Completa tus horas de la semana pasada',
      detalle: `Registraste ${h} h de ${meta / 60} h. Agrégalas en Mis horas para que se puedan aprobar.`,
      urgencia: 1,
      ir: { a: 'ruta', ruta: '/horas' },
    },
  ];
}

export type Leidos = { desde: string; leidos: Record<string, string> };

// Lo que paso antes de que la persona empezara a usar la campana cuenta como leido (no se llena de avisos viejos),
// salvo una reunion que empieza ya.
export const esNuevo = (a: Aviso, l: Leidos) => !l.leidos[a.id] && ((a.tipo === 'reunion' && a.urgencia === 3) || Date.parse(a.fecha) > Date.parse(l.desde));

export function filtrarPorPrefs(avisos: Aviso[], p: PrefNotif) {
  return avisos.filter((a) => {
    if (a.tipo === 'correo') return p.correos;
    if (a.tipo === 'cliente' || a.tipo === 'sla' || a.tipo === 'seguimiento') return p.casos;
    if (a.tipo === 'invitacion' || a.tipo === 'equipo') return p.invitaciones;
    return true;
  });
}

// Una sola lista sin repetidos: primero lo urgente que sigue sin ver, luego por fecha.
export function juntar(listas: Aviso[][], l: Leidos) {
  const vistos = new Set<string>();
  return listas
    .flat()
    .filter((a) => !vistos.has(a.id) && !!vistos.add(a.id))
    .sort((a, b) => {
      const ua = esNuevo(a, l) && a.urgencia === 3 ? 1 : 0;
      const ub = esNuevo(b, l) && b.urgencia === 3 ? 1 : 0;
      return ub - ua || Date.parse(b.fecha) - Date.parse(a.fecha);
    });
}

export type Grupo = 'ahora' | 'hoy' | 'antes';
export function grupoDe(a: Aviso, ahora = Date.now()): Grupo {
  const t = Date.parse(a.fecha);
  if ((a.tipo === 'reunion' && a.urgencia === 3) || ahora - t < 60 * MIN) return 'ahora';
  return new Date(t).toDateString() === new Date(ahora).toDateString() ? 'hoy' : 'antes';
}

// Marca leidos y olvida los que ya no estan en la lista (para que el almacen no crezca sin fin).
export function marcar(l: Leidos, ids: string[], vigentes: string[], ahora = Date.now()): Leidos {
  const quedan = new Set(vigentes);
  const leidos: Record<string, string> = {};
  for (const [k, v] of Object.entries(l.leidos)) if (quedan.has(k)) leidos[k] = v;
  for (const id of ids) leidos[id] = iso(ahora);
  return { desde: l.desde, leidos };
}

// Avisos que aun no se habian alertado (sonido y notificacion del navegador), de mayor a menor urgencia.
export function porAlertar(avisos: Aviso[], l: Leidos, alertados: Set<string>) {
  return avisos.filter((a) => esNuevo(a, l) && !alertados.has(a.id)).sort((a, b) => b.urgencia - a.urgencia);
}

const minutos = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return (h || 0) * 60 + (m || 0);
};
// No molestar: un horario que puede pasar la medianoche (19:00 a 07:00).
export function enNoMolestar(p: Pick<PrefNotif, 'noMolestar' | 'desde' | 'hasta'>, fecha = new Date()) {
  if (!p.noMolestar) return false;
  const t = fecha.getHours() * 60 + fecha.getMinutes();
  const a = minutos(p.desde);
  const b = minutos(p.hasta);
  return a <= b ? t >= a && t < b : t >= a || t < b;
}

// El sonido de un aviso segun lo que eligio la persona. Las reuniones a 5 min siempre tienen su propio sonido.
export function sonidoDe(a: Aviso, p: PrefNotif): Sonido {
  if (!p.sonido) return 'ninguno';
  const ya = a.tipo === 'reunion' && a.urgencia === 3;
  if (p.modoSonido === 'reuniones') return a.tipo === 'reunion' ? (ya ? 'reunion' : 'campanilla') : 'ninguno';
  if (ya) return 'reunion';
  if (p.modoSonido === 'suave') return 'suave';
  if (a.tipo === 'equipo') return 'ninguno';
  return a.urgencia >= 2 ? 'campanilla' : 'suave';
}
